import { replace } from "react-router-dom";
import { AggregatedAnalyticsSummary, AnalyticsRankingMetric, AverageViewsPerStreamResult, DailyLiveStreamStats, DailyLiveStreamStatsByTitle, DailySignupCount, DailySubscriptionAnalyticsResult, DailyUserSignupsResult, EnterpriseStreamDetail, EnterpriseStreamSummary, ExperienceAnalyticsForDay, ExperienceAnalyticsSummary, MinutesViewedOverTimeResult, RankingsResult, StreamedMinutesOverTimeResult, StreamingSummaryStats, StreamsOverTimeResult, TotalUserSignupsCount, ViewsOverTimeResult } from "../types/Analytics";
import { HttpError } from "../types/Error";
import { PagedResults } from "../types/PagedResults";
import useApiClient from "./ApiClient";

const useAnalyticsService = () => {
  const { get, post, put, delete: del } = useApiClient();

  const getTotalUserSignups = async (
    from?: Date,
    to?: Date
  ): Promise<TotalUserSignupsCount> => {
    try {
      // Use query parameters if from and to provided. 
      // Start with base address /admin/analytics/usersignups/total.
      const url = new URL(`/admin/analytics/usersignups/total`, window.location.origin);
      if (from) url.searchParams.append("from", from.toISOString());
      if (to) url.searchParams.append("to", to.toISOString());

      const result = await get<TotalUserSignupsCount>(url.toString().replace(window.location.origin, ""));

      // Check if the response is an error.
      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }

      return result as TotalUserSignupsCount;
    } catch(error) {
      console.error("Error getting total user signups:", error);
      throw error;
    }
  }

  const getDailyUserSignups = async (from?: Date, to?: Date): Promise<DailyUserSignupsResult> => {
    try {
      const url = new URL(`/admin/analytics/usersignups/daily`, window.location.origin);
      if (from) url.searchParams.append("from", from.toISOString());
      if (to) url.searchParams.append("to", to.toISOString());

      const result = await get<DailyUserSignupsResult>(url.toString().replace(window.location.origin, ""));

      // Check if the response is an error.
      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }

      return result as DailyUserSignupsResult;
    } catch (error) {
      console.error("Error getting daily user signups:", error);
      throw error;
    }
  }

  const getStreamingSummaryStats = async ():Promise<StreamingSummaryStats> => {
    try {
      const result = await get<StreamingSummaryStats>(`/admin/analytics/LiveStreaming/summaries`);

      // Check if the response is an error.
      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }

      return result as StreamingSummaryStats;

    } catch (error) {
      console.error("Error getting streaming summary stats:", error);
      throw error;
    }
  }


  const getLiveAppStreamsOverTime = async (from?: Date, to?: Date, groupByHour: boolean = false, titleId?:string, userId?:string): Promise<StreamsOverTimeResult> => { 
    try {
      const url = new URL(`/admin/analytics/LiveStreaming/StreamsOverTime`, window.location.origin);
      if (from) url.searchParams.append("from", from.toISOString());
      if (to) url.searchParams.append("to", to.toISOString());
      if (groupByHour) url.searchParams.append("groupByHour", String(groupByHour));
      if (titleId) url.searchParams.append("titleId", titleId);
      if (userId) url.searchParams.append("userId", userId);

      const result = await get<StreamsOverTimeResult>(url.toString().replace(window.location.origin, ""));

      // Check if the response is an error.
      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }

      return result as StreamsOverTimeResult;
    } catch (error) {
      console.error("Error getting total streams over time:", error);
      throw error;
    }
  }

  const getLiveAppStreamMinutesOverTime = async (from?: Date, to?: Date, groupByHour: boolean = false, titleId?: string, userId?: string, minMinutes?: number): Promise<StreamedMinutesOverTimeResult> => {
    try {
      const url = new URL(`/admin/analytics/LiveStreaming/StreamMinutesOverTime`, window.location.origin);
      if (from) url.searchParams.append("from", from.toISOString());
      if (to) url.searchParams.append("to", to.toISOString());
      if (groupByHour) url.searchParams.append("groupByHour", String(groupByHour));
      if (titleId) url.searchParams.append("titleId", titleId);
      if (userId) url.searchParams.append("userId", userId);
      if (minMinutes) url.searchParams.append("minMinutes", String(minMinutes));

      const result = await get<StreamedMinutesOverTimeResult>(url.toString().replace(window.location.origin, ""));

      // Check if the response is an error.
      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }

      return result as StreamedMinutesOverTimeResult;
    } catch (error) {
      console.error("Error getting live app stream minutes over time:", error);
      throw error;
    }
  }

  const getLiveAppViewsOverTime = async (from?: Date, to?: Date, groupByHour: boolean = false, platform?: string, titleId?:string, userId?: string, minViews?: number): Promise<ViewsOverTimeResult> => {
    try {
      const url = new URL(`/admin/analytics/LiveStreaming/ViewsOverTime`, window.location.origin);
      if (from) url.searchParams.append("from", from.toISOString());
      if (to) url.searchParams.append("to", to.toISOString());
      if (groupByHour) url.searchParams.append("groupByHour", String(groupByHour));
      if (platform) url.searchParams.append("platform", platform);
      if (titleId) url.searchParams.append("titleId", titleId);
      if (userId) url.searchParams.append("userId", userId);
      if (minViews) url.searchParams.append("minViews", String(minViews));

      const result = await get<ViewsOverTimeResult>(url.toString().replace(window.location.origin, ""));

      // Check if the response is an error.
      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }

      return result as ViewsOverTimeResult;
    } catch (error) {
      console.error("Error getting live app views over time:", error);
      throw error;
    }
  }

  const getLiveAppMinutesViewedOverTime = async (from?: Date, to?: Date, groupByHour: boolean = false, platform?: string, titleId?:string, userId?: string, minMinutes?: number): Promise<MinutesViewedOverTimeResult> => {
    try {
      const url = new URL(`/admin/analytics/LiveStreaming/MinutesViewedOverTime`, window.location.origin);
      if (from) url.searchParams.append("from", from.toISOString());
      if (to) url.searchParams.append("to", to.toISOString());
      if (groupByHour) url.searchParams.append("groupByHour", String(groupByHour));
      if (platform) url.searchParams.append("platform", platform);
      if (titleId) url.searchParams.append("titleId", titleId);
      if (userId) url.searchParams.append("userId", userId);
      if (minMinutes) url.searchParams.append("minMinutes", String(minMinutes));

      const result = await get<MinutesViewedOverTimeResult>(url.toString().replace(window.location.origin, ""));

      // Check if the response is an error.
      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }

      return result as MinutesViewedOverTimeResult;
    } catch (error) {
      console.error("Error getting live app minutes viewed over time:", error);
      throw error;
    }
  }

  const getLiveAppAverageViewsPerStream = async (from?: Date, to?: Date, groupByHour: boolean = false, platform?: string, titleId?: string, userId?: string): Promise<AverageViewsPerStreamResult> => {
    try {
      const url = new URL(`/admin/analytics/LiveStreaming/AverageViewsPerStream`, window.location.origin);
      if (from) url.searchParams.append("from", from.toISOString());
      if (to) url.searchParams.append("to", to.toISOString());
      if (groupByHour) url.searchParams.append("groupByHour", String(groupByHour));
      if (platform) url.searchParams.append("platform", platform);
      if (titleId) url.searchParams.append("titleId", titleId);
      if (userId) url.searchParams.append("userId", userId);

      const result = await get<AverageViewsPerStreamResult>(url.toString().replace(window.location.origin, ""));

      // Check if the response is an error.
      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }

      return result as AverageViewsPerStreamResult;
    } catch (error) {
      console.error("Error getting live app average views per stream:", error);
      throw error;
    }
  }

  const getDailyLiveStreamStats = async (from?: Date, to?: Date): Promise<DailyLiveStreamStats> => {
    try {
      const url = new URL(`/admin/analytics/LiveStreaming/Daily`, window.location.origin);
      if (from) url.searchParams.append("from", from.toISOString());
      if (to) url.searchParams.append("to", to.toISOString());
      const result = await get<DailyLiveStreamStats>(url.toString().replace(window.location.origin, ""));

      // Check if the response is an error.
      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }
      return result as DailyLiveStreamStats;
    } catch (error) {
      console.error("Error getting daily live stream stats:", error);
      throw error;
    }
  }

  const getDailyLiveStreamForUserStats = async (userId: string, from?: Date, to?: Date): Promise<DailyLiveStreamStats> => {
    try {
      const url = new URL(`/admin/analytics/LiveStreaming/Daily/user/${userId}`, window.location.origin);
      if (from) url.searchParams.append("from", from.toISOString());
      if (to) url.searchParams.append("to", to.toISOString());
      const result = await get<DailyLiveStreamStats>(url.toString().replace(window.location.origin, ""));

      // Check if the response is an error.
      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }
      return result as DailyLiveStreamStats;
    } catch (error) {
      console.error("Error getting daily live stream stats:", error);
      throw error;
    }
  }

  const getDailyLiveStreamForTitleStats = async (titleId: string, from?: Date, to?: Date): Promise<DailyLiveStreamStatsByTitle> => {
    try {
      const url = new URL(`/admin/analytics/LiveStreaming/Daily/title/${titleId}`, window.location.origin);
      if (from) url.searchParams.append("from", from.toISOString());
      if (to) url.searchParams.append("to", to.toISOString());
      const result = await get<DailyLiveStreamStatsByTitle>(url.toString().replace(window.location.origin, ""));

      // Check if the response is an error.
      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }
      return result as DailyLiveStreamStatsByTitle;
    } catch (error) {
      console.error("Error getting daily live stream stats:", error);
      throw error;
    }
  }

  const getLiveAppRankings = async (from?: Date, to?: Date, rankingMetric?: AnalyticsRankingMetric, byGame: boolean = true, limit: number = 10, platform?: string): Promise<RankingsResult> => {
    try {
      const url = new URL(`/admin/analytics/LiveStreaming/Rankings`, window.location.origin);
      if (from) url.searchParams.append("from", from.toISOString());
      if (to) url.searchParams.append("to", to.toISOString());
      if (rankingMetric) url.searchParams.append("metric", String(rankingMetric));
      url.searchParams.append("byGame", String(byGame));
      url.searchParams.append("limit", String(limit));
      if (platform) url.searchParams.append("platform", platform);

      const result = await get<RankingsResult>(url.toString().replace(window.location.origin, ""));

      // Check if the response is an error.
      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }

      return result as RankingsResult;
    } catch (error) {
      console.error("Error getting live app rankings:", error);
      throw error;
    }
  }

  const searchExperienceData = async (
    searchTerm?: string,
    page?: number,
    orderBy?: string,
    orderDirection?: string,
    itemsPerPage?: number
  ): Promise<PagedResults<ExperienceAnalyticsSummary>> => {
    try {
      const postData = {
        searchTerm,
        page: page ?? 1,
        itemsPerPage: itemsPerPage ?? 10,
        orderBy: orderBy ?? "count",
        orderDirection: orderDirection ?? "desc",
      };

      const result = await post<PagedResults<ExperienceAnalyticsSummary>>(
        `/admin/analytics/search`,
        postData
      );

      // Check if the response is an error.
      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }

      return result as PagedResults<ExperienceAnalyticsSummary>;
    } catch (error) {
      console.error("Error getting summed experience data:", error);
      throw error;
    }
  };

  const getExperienceSummary = async (companyId: string, experienceId: string):Promise<ExperienceAnalyticsSummary> => {
    try {
      const result = await get<ExperienceAnalyticsSummary>(
        `/company/${companyId}/Analytics/Experience/${experienceId}`
      );

      // Check if the response is an error.
      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }

      return result as ExperienceAnalyticsSummary;
    } catch (error) {
      console.error("Error getting experience summary:", error);
      throw error;
    }
  }

  const getAggregatedExperienceSummary = async (): Promise<AggregatedAnalyticsSummary> => {
    try {
      const result = await get<ExperienceAnalyticsSummary>(
        `/admin//Analytics/Summary`
      );

      // Check if the response is an error.
      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }

      return result as AggregatedAnalyticsSummary;
    } catch (error) {
      console.error("Error getting aggregated summary:", error);
      throw error;
    }
  }

  const getExperienceAnalyticsByHour = async (
    companyId: string,
    experienceId: string,
    date: string
  ) : Promise<ExperienceAnalyticsForDay> => {
    try {
      const result = await get<ExperienceAnalyticsForDay>(
        `/company/${companyId}/Analytics/Experience/${experienceId}/${date}`
      );

      // Check if the response is an error.
      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }

      return result as ExperienceAnalyticsForDay;
    } catch (error) {
      console.error("Error getting experience analytics by hour:", error);
      throw error;
    }
  }

  const getEnterpriseStreamsSummary = async (from?: Date, to?: Date): Promise<EnterpriseStreamSummary[]> => {
    try {
      const url = new URL(`/admin/analytics/EnterpriseStreams/Summary`, window.location.origin);
      if (from) url.searchParams.append("from", from.toISOString());
      if (to) url.searchParams.append("to", to.toISOString());

      const result = await get<EnterpriseStreamSummary[]>(url.toString().replace(window.location.origin, ""));

      // Check if the response is an error.
      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }

      return result as EnterpriseStreamSummary[];
    } catch (error) {
      console.error("Error getting enterprise streams summary:", error);
      throw error;
    }
  }

  const getEnterpriseStreamDetail = async (experienceId: string, from?: Date, to?: Date): Promise<EnterpriseStreamDetail> => {
    try {
      const url = new URL(`/admin/analytics/EnterpriseStreams/${experienceId}`, window.location.origin);
      if (from) url.searchParams.append("from", from.toISOString());
      if (to) url.searchParams.append("to", to.toISOString());

      const result = await get<EnterpriseStreamDetail>(url.toString().replace(window.location.origin, ""));

      // Check if the response is an error.
      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }

      return result as EnterpriseStreamDetail;
    } catch (error) {
      console.error("Error getting enterprise stream detail:", error);
      throw error;
    }
  }

  const getDailySubscriptionAnalytics = async (from?: Date, to?: Date): Promise<DailySubscriptionAnalyticsResult> => {
    try {
      const url = new URL(`/admin/analytics/Subscriptions/Daily`, window.location.origin);
      if (from) url.searchParams.append("from", from.toISOString());
      if (to) url.searchParams.append("to", to.toISOString());

      const result = await get<DailySubscriptionAnalyticsResult>(url.toString().replace(window.location.origin, ""));

      if ((result as HttpError).errorCode) {
        throw (result as HttpError).description;
      }

      return result as DailySubscriptionAnalyticsResult;
    } catch (error) {
      console.error("Error getting daily subscription analytics:", error);
      throw error;
    }
  }

  return {
    getTotalUserSignups,
    getDailyUserSignups,
    getDailySubscriptionAnalytics,
    getLiveAppStreamsOverTime,
    getLiveAppStreamMinutesOverTime,
    getLiveAppViewsOverTime,
    getLiveAppMinutesViewedOverTime,
    getLiveAppAverageViewsPerStream,
    getLiveAppRankings,
    getAggregatedExperienceSummary,
    searchExperienceData,
    getExperienceSummary,
    getExperienceAnalyticsByHour,
    getStreamingSummaryStats,
    getDailyLiveStreamStats,
    getDailyLiveStreamForTitleStats,
    getDailyLiveStreamForUserStats,
    getEnterpriseStreamsSummary,
    getEnterpriseStreamDetail
  };
};

export default useAnalyticsService;
